"use client";

import { useCallback, useState } from "react";
import { Share2 } from "lucide-react";
import { event } from "@/lib/gtag";
import type { Locale } from "@/lib/i18n/locales";
import {
  quizResultsCopy,
  buildQuizShareText,
} from "@/lib/i18n/quiz-results-copy";

type ShareQuizResultProps = {
  locale?: Locale;
  quizTitle: string;
  score: number;
  total: number;
  /** Absolute URL to the quiz or notes page. */
  shareUrl?: string;
  className?: string;
};

export default function ShareQuizResult({
  locale = "en",
  quizTitle,
  score,
  total,
  shareUrl,
  className,
}: ShareQuizResultProps) {
  const copy = quizResultsCopy(locale);
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const text = buildQuizShareText(quizTitle, score, total, locale);
    const url = shareUrl ?? window.location.href;

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ text, url });
        event("quiz_share", {
          method: "native_share",
          quiz_title: quizTitle,
          score,
          total,
        });
        return;
      } catch {
        // user cancelled or share API unavailable - fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      event("quiz_share", {
        method: "clipboard_copy",
        quiz_title: quizTitle,
        score,
        total,
      });
    } catch {
      // clipboard API blocked - silent fail
    }
  }, [quizTitle, score, total, shareUrl, locale]);

  return (
    <button
      type="button"
      onClick={handleShare}
      className={
        className ??
        "inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
      }
    >
      <Share2 className="h-4 w-4" />
      {copied ? copy.copied : copy.share}
    </button>
  );
}
